import { FormControl, FormControlLabel, Radio, RadioGroup, Tooltip } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useActionRequest, useDataRequest } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { Fmt } from "../../../../../formatters";
import HelpIcon from "@mui/icons-material/Help";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { Callout, CalloutVariant } from "../../../../Callout";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import "../../../../../../../sass/components/verify-consumer.scss";
import { DataRequestCustomFieldDisplay } from "../../DataRequestCustomFieldDisplay";
import { useOrganizationVendors } from "../../../../../hooks";
import { BoltIcon } from "@heroicons/react/20/solid";
import { AlertCard, VendorLogo } from "@truevault/aquilo";

export const VerifyConsumer = () => {
  const [org] = usePrimaryOrganization();

  const [allVendors, ,] = useOrganizationVendors(
    org?.id,
    // All following args are default values from function signature, except for onlyUseSettled
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ true,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
  );

  const instructionsApi = useCallback(
    () => Api.requestHandlingInstructions.getCGInstructions(org.id),
    [org.id],
  );

  const { result: fetchedInstructions } = useDataRequest({
    queryKey: ["instructions", org.id],
    api: instructionsApi,
  });

  const { request, refreshRequest, setStepCompleted } = useDataRequestProcessingContext();
  const [subjectStatus, setSubjectStatus] = useState<string>(request?.consumerGroupResult ?? "");

  const fullName = Fmt.DataSubject.fmtFullName(request);
  const outcomes = request.vendorOutcomes || [];

  const { fetch: updateStatus } = useActionRequest({
    api: async (args = {}) => {
      const { status, resetState } = args;
      try {
        const resp = await Api.dataRequest.setSubjectStatus(org.id, request.id, {
          subjectStatus: status,
        });
        if (resp.id == undefined) {
          resetState();
        }
        refreshRequest();
      } catch (err) {
        resetState();
        throw err;
      }
    },
    onError: refreshRequest,
  });

  const onSelectionChange = (e) => {
    const status = e.target.value;
    const resetState = () => {
      const currentStatus = subjectStatus;
      setSubjectStatus(() => currentStatus);
    };

    setSubjectStatus(status);

    updateStatus({ status: status, resetState: resetState });
  };

  const everContacted = request.requestorContacted || request.vendorContacted;

  useEffect(() => {
    setStepCompleted(!!request.consumerGroupResult);
  }, [request, setStepCompleted]);

  const helpText =
    "Select if you processed this consumer's data solely on behalf of another business";

  useEffect(() => {
    setSubjectStatus(request.consumerGroupResult ?? "");
  }, [request.consumerGroupResult]);

  let primaryLookupVendor = org.primaryLookupSystemVendorId
    ? allVendors.find((v) => v.vendorId === org.primaryLookupSystemVendorId)
    : null;
  let secondaryLookupVendor = org.secondaryLookupSystemVendorId
    ? allVendors.find((v) => v.vendorId === org.secondaryLookupSystemVendorId)
    : null;

  // Technically this is a slight fib, we don't know *for sure* that the consumer was found specifically in one of these
  // vendors: they could have an unrelated Delete workflow that finds the consumer in a 3rd vendor that isn't one of the
  // two here - but it doesn't really matter, the point is the consumer was found automatically, the UI here is just for
  // window dressing. The more specific `foundAutomaticallyInLookupVendor` therefore controls whether we show the vendor
  // names from the lookup list or show the automation-confirmed vendor.
  const foundAutomatically = request.consumerGroupResult === "FOUND_AUTOMATICALLY";
  const foundAutomaticallyInLookupVendor = outcomes
    .filter((o) => o.outcome === "AUTOMATION_CONFIRMED")
    .some((o) => {
      return [org.primaryLookupSystemVendorId, org.secondaryLookupSystemVendorId].includes(
        o.vendor.id,
      );
    });
  const arbitraryAutomaticVendor = outcomes.find((o) => o.outcome === "AUTOMATION_CONFIRMED");

  const lookupVendorListInner = (
    <>
      {primaryLookupVendor && (
        <VendorNameWithLetterIcon
          vendorName={primaryLookupVendor.name}
          vendorLogoUrl={primaryLookupVendor.logoUrl}
        />
      )}
      {primaryLookupVendor && secondaryLookupVendor && (
        <div className="text-neutral-800 font-normal text-[16px]">or</div>
      )}
      {secondaryLookupVendor && (
        <VendorNameWithLetterIcon
          vendorName={secondaryLookupVendor.name}
          vendorLogoUrl={secondaryLookupVendor.logoUrl}
        />
      )}
    </>
  );

  const lookupVendorList = (
    <div className="tw-boundary" style={{ display: foundAutomatically ? "inline-block" : "block" }}>
      <div className="flex items-center gap-[6px] my-[18px]">
        {!foundAutomatically && (
          <>
            <div className="text-neutral-800 font-normal text-[16px]">Search for data in:</div>
            {lookupVendorListInner}
          </>
        )}
        {foundAutomatically &&
          (foundAutomaticallyInLookupVendor ? (
            lookupVendorListInner
          ) : arbitraryAutomaticVendor ? (
            <VendorNameWithLetterIcon
              vendorName={arbitraryAutomaticVendor.vendor.name}
              vendorLogoUrl={arbitraryAutomaticVendor.vendor.logoUrl}
            />
          ) : (
            lookupVendorListInner
          ))}
      </div>
    </div>
  );

  const consumerGroupResultFields = (
    <FormControl component="fieldset">
      <RadioGroup
        aria-label="Verify Consumer Options"
        name="verify-consumer-options"
        onChange={onSelectionChange}
        value={subjectStatus}
      >
        <FormControlLabel
          value={foundAutomatically ? "FOUND_AUTOMATICALLY" : "FOUND"}
          control={<Radio />}
          label={
            foundAutomatically ? (
              <span className={"text-t2"} style={{ display: "flex", alignItems: "center" }}>
                <span className="tw-boundary mr-xs">
                  <BoltIcon className="h-[20px] w-[20px] text-[#017F4D] bg-[#E2FDF3] p-0.5 rounded-3xl" />
                </span>
                <span className="mr-xs">Yes, data found in:</span>
                {lookupVendorList}
              </span>
            ) : (
              <span className={"text-t2"}>Yes, we have data about them</span>
            )
          }
          style={{ marginBottom: "6px" }}
          disabled={everContacted}
        />
        <FormControlLabel
          control={<Radio />}
          value="NOT_FOUND"
          label={<span className={"text-t2"}>No, I can't find a record of them</span>}
          style={{ marginBottom: "6px" }}
          disabled={everContacted || foundAutomatically}
        />
        {org?.isServiceProvider && (
          <FormControlLabel
            value="PROCESSED_AS_SERVICE_PROVIDER"
            control={<Radio />}
            label={
              <span className={"text-t2 info-wrapper"}>
                Processed as a Service Provider
                <Tooltip className="info-tooltip" title={helpText} placement="top">
                  <HelpIcon className="question-icon" />
                </Tooltip>
              </span>
            }
            style={{ marginBottom: "6px" }}
            disabled={everContacted}
          />
        )}
      </RadioGroup>
    </FormControl>
  );

  return (
    <>
      {request.emailVerified && (
        <div className="tw-boundary">
          <AlertCard
            variant="success"
            title={`${fullName}'s email has been verified${
              request.emailVerifiedAutomatically
                ? " by TrueVault"
                : request.emailVerifiedManually
                ? " by your organization"
                : ""
            }.`}
          />
        </div>
      )}

      <DataRequestCustomFieldDisplay
        org={org}
        request={request}
        classList={fetchedInstructions ? "mb-std" : "mb-md"}
      />

      <div className="tw-boundary">
        {!foundAutomatically &&
          outcomes
            .filter((o) => o.outcome === "AUTOMATION_NO_DATA")
            .map((o) => {
              const vendor = allVendors.find((v) => v.vendorId === o.vendor.id);
              if (!vendor) return null;
              return (
                <AlertCard key={o.vendor.id} variant={"info"}>
                  <p>
                    No data found in <strong>{vendor.name}</strong>
                  </p>
                </AlertCard>
              );
            })}
      </div>
      <div className="text-t3 mb-0">Does your business have data about this consumer?</div>

      {(primaryLookupVendor || secondaryLookupVendor) && !foundAutomatically && (
        <>{lookupVendorList}</>
      )}

      {Boolean(fetchedInstructions) && !foundAutomatically && (
        <Callout variant={CalloutVariant.Clear} className={"mb-std"}>
          <p>{fetchedInstructions}</p>
        </Callout>
      )}

      {consumerGroupResultFields}
    </>
  );
};

function VendorNameWithLetterIcon({
  vendorName,
  vendorLogoUrl = null,
}: {
  vendorName: string;
  vendorLogoUrl?: string;
}) {
  return (
    <div className="flex items-center gap-[6px]">
      <VendorLogo vendorName={vendorName} vendorLogoUrl={vendorLogoUrl} />
      <div className="text-black text-[14px] font-normal">{vendorName}</div>
    </div>
  );
}
